<template>
    <div :class="classes.root">
        <!-- Type -->
        <field :visible="allowed('type')"
               :label-width="labelWidth"
               label="Тип"
               :value="task.translated_type"
               value-color="accent"
               :direction-column="directionColumn" />

        <!-- Created at -->
        <field :visible="allowed('createdAt')"
               :label-width="labelWidth"
               label="Создано"
               :value="dateDistanceToNow(task.created_at)"
               :direction-column="directionColumn" />

        <!-- Start at -->
        <field :visible="allowed('startAt')"
               :label-width="labelWidth"
               label="Дата приемки"
               :value="startAt"
               :direction-column="directionColumn" />

        <!-- Updated at -->
        <field :visible="allowed('updatedAt')"
               :label-width="labelWidth"
               label="Обновлено"
               :value="dateDistanceToNow(task.updated_at)"
               :direction-column="directionColumn" />

        <!-- Status -->
        <field :visible="allowed('status')"
               :label-width="labelWidth"
               label="Статус"
               :direction-column="directionColumn">
            <task-status :task="task" />
        </field>

        <!-- Kind of defect -->
        <field :visible="allowed('kindDefect')"
               :label-width="labelWidth"
               label="Вид дефекта"
               :value="translatedDefectKind"
               :direction-column="directionColumn" />
  
        <!-- Expired at -->
        <field :visible="allowed('expiredAt') && !!convertedTime"
               :label-width="labelWidth"
               label="Дата завершения"
               :value="convertedTime | dateFormat(timeZone, 'dd.MM.y')"
               :direction-column="directionColumn" />

        <!-- Initiator -->
        <field :visible="allowed('initiator')"
               :label-width="labelWidth"
               label="Инициатор"
               :direction-column="directionColumn">
            <user-thumb v-if="initiator"
                        :user="initiator"
                        with-organization />

            <our-label v-else
                       :value="labels.undefined" />
        </field>

        <!-- Mananger -->
        <field :visible="allowed('manager') && !!manager"
               :label-width="labelWidth"
               label="Руководитель строительства Г/П"
               :direction-column="directionColumn">
            <user-thumb v-if="manager"
                        :user="manager"
                        with-organization />

            <our-label v-else
                       :value="labels.undefined" />
        </field>

        <!-- Inspector (for building order) -->
        <field v-if="isBuildingOrderType"
               :visible="allowed('inspector') && !!inspector"
               :label-width="labelWidth"
               label="Инженер СК Г/П"
               :direction-column="directionColumn">
            <user-thumb v-if="inspector"
                        :user="inspector"
                        with-organization />

            <our-label v-else
                       :value="labels.undefined" />
        </field>

        <!-- Inspector -->
        <field v-else
               :visible="allowed('inspector')"
               :label-width="labelWidth"
               label="Контролер"
               :direction-column="directionColumn">
            <user-thumb v-if="inspector"
                        :user="inspector"
                        with-organization />

            <our-label v-else
                       :value="labels.undefined" />
        </field>

        <!-- Workers for defect -->
        <field v-for="worker in workers"
               :key="worker.id"
               :visible="allowed('worker')"
               :label-width="labelWidth"
               :label="worker.translated_sub_role || worker.translated_role"
               :direction-column="directionColumn">
            <user-thumb :user="worker.user"
                        with-organization />
        </field>

        <!-- Workers for acceptance (first stage) -->
        <field :visible="allowed('engineer')"
               :label-width="labelWidth"
               label="Инженер СК Г/П"
               :direction-column="directionColumn">
            <div class="f f-col">
                <user-thumb v-for="worker in acceptanceWorkWorkersFirstStage"
                            :key="worker.id"
                            :user="worker.user"
                            with-organization />

                <our-label v-if="!hasAcceptanceWorkWorkersFirstStage"
                           :value="labels.undefined" />
            </div>
        </field>

        <!-- Workers for acceptance (second stage) -->
        <field :visible="allowed('engineer') && hasAcceptanceWorkWorkersSecondStage"
               :label-width="labelWidth"
               label="Инженер СКЗ"
               :direction-column="directionColumn">
            <div class="f f-col">
                <user-thumb v-for="worker in acceptanceWorkWorkersSecondStage"
                            :key="worker.id"
                            :user="worker.user"
                            with-organization />

                <our-label v-if="!hasAcceptanceWorkWorkersSecondStage"
                           :value="labels.undefined" />
            </div>
        </field>

        <!-- Contractor -->
        <field :visible="allowed('contractor')"
               :label-width="labelWidth"
               label="Подрядчик"
               :direction-column="directionColumn">
            <user-thumb v-if="contractor"
                        :user="contractor"
                        with-organization />

            <our-label v-else
                       :value="labels.undefined" />
        </field>

        <!-- Inspector (for defect) -->
        <field :visible="allowed('generalContractor')"
               :label-width="labelWidth"
               label="Контроль"
               :direction-column="directionColumn">
            <div v-if="inspectors.length">
                <user-thumb v-for="user in inspectors"
                            :key="user.id"
                            :user="user"
                            with-organization />
            </div>

            <our-label v-else
                       :value="labels.undefined" />
        </field>

        <!-- KS2 -->
        <field :visible="allowed('astActs') && !!astActs?.length"
               :label-width="labelWidth"
               label="КС-2 в АСТ">
            <div class="f-col space-y-0.25">
                <our-label v-for="act in astActs"
                           :key="act.id"
                           :value="act.ast_id" />
            </div>
        </field>

        <!-- Jobs -->
        <field :visible="allowed('jobTypes')"
               :label-width="labelWidth"
               label="Виды работ"
               :direction-column="directionColumn">
            <div class="f-col">
                <our-label v-for="(job, index) in jobTypes(task.data.job_types)"
                           :key="index"
                           :value="job" />
            </div>
        </field>

        <!-- Contract -->
        <field :visible="allowed('astContract') && !!task.data.ast_contract"
               :label-width="labelWidth"
               label="Договор"
               :value="astContract" />
        
        <!-- Contract works -->
        <field :visible="allowed('astWorks') && !!task.data.ast_works?.length"
               :label-width="labelWidth" 
               label="Принимаемые работы:"
               direction="vertical">
            <contract-work-facts class="w-full"
                                 :task-id="task.id"
                                 :works="contractWorks"
                                 :contract="task.data.ast_contract"
                                 with-last-fact
                                 with-open-dialog />
        </field>
        
        <!-- Comment -->
        <field :visible="allowed('commentInitiator')"
               :label-width="labelWidth"
               label="Принимаемые работы"
               :value="getNested(task, 'data.comment_initiator')"
               :direction-column="directionColumn" />

        <!-- Related tasks defect -->
        <access permissions="project_tasks_defects_show"
                :allowed="allowed('relatedTasksDefect')"
                hidable>
            <field :label-width="labelWidth"
                   label="Связанные предписания:"
                   :direction-column="directionColumn">
                <select-task-defect-field
                    :organization-id="contractor?.organization?.id"
                    show-list
                    label-compact
                    readonly
                    independent />
            </field>
        </access>
        
        <!-- Axis -->
        <field :visible="allowed('axis')"
               :label-width="labelWidth"
               label="В осях"
               :value="getNested(task, 'data.axis')"
               :direction-column="directionColumn" />

        <!-- Engineer comment -->
        <field :visible="allowed('commentWorker') && !!task.data.comment_worker"
               :label-width="labelWidth"
               :label="aboutCommentWorker"
               :value="getNested(task, 'data.comment_worker')"
               :direction-column="directionColumn" />

        <!-- General engineer comment -->
        <field :visible="allowed('commentWorkerSecondStage') && !!task.data.comment_worker_second_stage"
               :label-width="labelWidth"
               label="Комментарий инженера СКЗ"
               :value="getNested(task, 'data.comment_worker_second_stage')"
               :direction-column="directionColumn" />

        <!-- Worker comment -->
        <field :visible="allowed('commentContractor') && !!task.data.comment_contractor"
               :label-width="labelWidth"
               label="Комментарий подрядчика"
               :value="getNested(task, 'data.comment_contractor')"
               :direction-column="directionColumn" />

        <!-- Comments -->
        <field v-for="comment in comments"
               :key="comment.key"
               :label-width="labelWidth"
               :label="comment.label"
               :value="comment.value"
               :direction-column="directionColumn" />

        <!-- Task rejections -->
        <div v-if="isAcceptanceType && taskRejections.length"
             class="_ml-9">
            <div v-for="rejection in taskRejections"
                 :key="rejection.id">
                <el-link v-for="defect in rejection.task_reason_defects"
                         :key="defect.id"
                         type="primary"
                         @click="goToDefect(defect.id)">
                    Предписание №{{ defect.number_in_project }} {{ defect.name }}
                </el-link>
            </div>
        </div>

        <!-- PWP document -->
        <field v-if="shouldRequirePWP || documentOfPWP"
               v-loading="downloadLoading"
               :visible="allowed('documents')"
               :label-width="labelWidth"
               label="ППР"
               :direction-column="directionColumn">
            <!-- Viewing -->
            <div v-if="documentOfPWP"
                 class="cursor-pointer hover:bg-gray-100 pright-0.3"
                 style="margin-left: -0.6rem"
                 @click="openDocumentOfPWP">
                <document-thumb :document="documentOfPWP"
                                with-icon
                                with-type>
                    <div v-if="false"
                         class="f-x-end">
                        <tag v-if="documentOfPWPStatus"
                             :label="documentOfPWPStatus"
                             :color="documentOfPWPColor" />
                    </div>
                </document-thumb>
            </div>

            <!-- Empty -->
            <tag v-else
                 :label="labels.undefined" /> 
        </field>

        <!-- Entities -->
        <task-entities v-if="fieldToDisplay.includes('objects')"
                       :task="task"
                       :editable-fields="editableFields"
                       :editable="showAcceptanceEditButtons" />

        <portal-target name="allowed-actions" />

        <div v-if="isAcceptanceType && taskRejectionsByAstWorks.length">
            <span class="text-red-1 fs-0.875 space-x-0.25">
                {{ labels.acceptanceBlocked }}
                <el-link 
                    v-for="defect in taskRejectionsByAstWorks" 
                    :key="defect.id"
                    type="primary"
                    class="font-default"
                    @click="goToDefect(defect.id)">
                    №{{ defect.number_in_project }}
                </el-link>
            </span>
        </div>
    </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';

import { resourceable } from '@/store/connectors';
 
import { DEFECT_KINDS, TASK_MEMBER_SUB_ROLES } from '@/utils/tasks';
import { formatLabelWithDistance, dateToLocal, labelAt } from '@/utils/date'
import { openNew } from '@/utils/browser';
import { wordOfNumber } from '@/utils/format';

import { 
  types as taskTypes, 
  getDocumentsByType, 
  isWaitInformation, 
  getContractorUser,
  getWorkers,
  getManagerUser, 
  getInspectorUsers, 
  getComments, 
  getAstActs,
  getTaskRejections,
  getTaskRejectionsByAstWorks
} from '@/models/tasks'
import { types as documentTypes, getProperty } from '@/models/documents'
import { getContractDate, getContractNumber } from '@/models/contracts';

import Field from '@/components/task/TaskDescriptionField.vue'
import TaskEntities from '@/components/task/TaskEntities.vue'
import UserThumb from '@/components/users/UserThumb.vue'
import DocumentThumb from '@/components/documents/DocumentThumb.vue'
import TaskStatus from '@/components/tasks/TaskStatus.vue'
import ContractWorkFacts from '@/components/contracts/ContractWorkFacts.vue';
import SelectTaskDefectField from '@/components/fields/SelectTaskDefectField.vue';

const labels = {
  undefined: 'Не указано'
}

export default {
  name: 'TaskDescription',
  components: {
    Field,
    TaskEntities,
    UserThumb,
    DocumentThumb,
    TaskStatus,
    ContractWorkFacts,
    SelectTaskDefectField
  },

  mixins: [
    resourceable({ on: 'ast', name: 'contractWorks' })
  ],

  props: {
    task: { type: Object, default: () => ({}) },
    editableFields: { type: Array, default: () => [] },
    fieldToDisplay: { type: Array, default: () => [] },
    directionColumn: { type: Boolean, default: false },
    showAcceptanceEditButtons: { type: Boolean, default: false }
  },
  data() {
    return {
      downloadLoading: false
    }
  },
  computed: {
    ...mapGetters('projects', ['shouldRequirePWP']),
    ...mapGetters('tasks', ['isAcceptanceType']),
    ...mapGetters('floorPlans', ['plans']),

    isDefectsType() { return this.task.type === taskTypes.DEFECTS_AND_VIOLATIONS },
    isBuildingOrderType() { return this.task.type === taskTypes.BUILDING_ORDER },
    isPhotoOrderType() { return this.task.type === taskTypes.PHOTO_ORDER },
    isAcceptanceType() { return this.task.type === taskTypes.ACCEPTANCE_OF_WORK },

    labelWidth() {
      return this.directionColumn ? '100%' : '8'
    },

    labels() {
      return {
        ...labels,
        acceptanceBlocked: `Приемка работ будет заблокирована, пока подрядчик не устранит ${wordOfNumber(this.taskRejectionsByAstWorks.length, ['Предписание', 'предписания'])}`
      }
    },

    classes() {
      return {
        root: {
          'f-col w-full space-y-0.5': true,
          'space-y-1': this.directionColumn
        }
      }
    },

    startAt() {
      return labelAt(this.task.start_at, { withTime: false, withAlias: false })
    },

    initiator() {
      return this.task?.initiator?.user
    },

    contractor() {
      return getContractorUser(this.task)
    },

    inspector() {
      return this.task?.inspectors?.[0]?.user
    },

    inspectors() {
      return getInspectorUsers(this.task)
    },

    manager() {
      return getManagerUser(this.task)
    },

    workers() {
      return getWorkers(this.task)
    },

    acceptanceWorkWorkersFirstStage() {
      return this.workers.filter(w => w.sub_role === TASK_MEMBER_SUB_ROLES.WORKER_ACCEPTANCE_TASK_CONTROL_ENGINEER_FIRST_STAGE)
    },

    hasAcceptanceWorkWorkersFirstStage() {
      return !!this.acceptanceWorkWorkersFirstStage.length
    },

    acceptanceWorkWorkersSecondStage() {
      return this.workers.filter(w => w.sub_role === TASK_MEMBER_SUB_ROLES.WORKER_ACCEPTANCE_TASK_CONTROL_ENGINEER_SECOND_STAGE)
    },

    hasAcceptanceWorkWorkersSecondStage() {
      return !!this.acceptanceWorkWorkersSecondStage.length
    },

    hasWorkers() {
      return !!this.workers.length
    },

    status() {
      return this.task.translated_result.value
    },

    convertedTime() {
      return dateToLocal(this.task.expired_at, this.timeZone)
    },

    documentOfPWP() {
      return getDocumentsByType(this.task, documentTypes.PROJECT_WORK_PRODUCTION)[0]
    },

    documentOfPWPLabel() {
      return this.documentOfPWP && 'Прикреплен'
    },

    documentOfPWPStatus() {
      return !isWaitInformation(this.task) && getProperty(this.documentOfPWP, 'check_status_translated')
    },

    documentOfPWPColor() {
      return {
        [getProperty(this.documentOfPWP, 'check_status') === 'non_checked']: 'orange-1',
        [getProperty(this.documentOfPWP, 'check_status') === 'checked']: 'green-1',
        [getProperty(this.documentOfPWP, 'check_status') === 'decline']: 'red-1'
      }[true]
    },

    comments() {
      return getComments(this.task)
    },

    aboutCommentWorker() {
      return {
        [this.isAcceptanceType]: 'Комментарий инженера СК Г/П:',
        [this.isPhotoOrderType]: 'Комментарий подрядчика:'
      }[true]
    },

    acceptanceObjectPath: () => objects => {
      return objects.map(({project_object_structure}) => {
        const { house, floor } = project_object_structure;
        const houseStreet = house.street ? house.street : '';
        const houseNumber = house.number ? house.number : '';
        const houseName = `${houseStreet} ${houseNumber}`;
        const floorName = floor.number ? `Этаж ${floor.number}` : '';

        return `${houseName} / ${floorName}`;
      })
    },
    dateDistanceToNow: () => date => {
      return formatLabelWithDistance(date, { withCapital: true })
    },
    astActs() {
      return getAstActs(this.task)
    },
    jobTypes:() => types => {
      return Array.isArray(types) ? types.map(({code, name}) => `${code}. ${name}`) || [] : ''
    },

    astContract() {
      const contract = this.task.data.ast_contract

      return `${getContractNumber(contract)} от ${getContractDate(contract)}`
    },

    translatedDefectKind() {
      const {kind_defect} = this.task.data
      switch (kind_defect) {
      case DEFECT_KINDS.SIGNIFICANT:
        return  'Значительный'
      case DEFECT_KINDS.CRITICAL:
        return  'Критический'
      default:
        return 'Устранимый'
      }
    },

    taskRejections() {
      return getTaskRejections(this.task) || []
    },

    taskRejectionsByAstWorks() {
      return getTaskRejectionsByAstWorks(this.task) || []
    }
  },
  methods: {
    ...mapActions('documents', ['download']),

    allowed(field) {
      return this.fieldToDisplay.includes(field)
    },

    async openDocumentOfPWP() {
      this.downloadLoading = true

      this.download({ document: this.documentOfPWP })
        .then(window.open)
        .finally(() => this.downloadLoading = false)
    },

    goToDefect(id) {
      const r = this.$router.resolve({ name: 'project.task.info', params: { taskId: id } })

      const { href } = r || {}

      href && openNew(href)
    }
  }
};
</script>
